import { Icon, IconSize, Position } from "@blueprintjs/core"
import { TimePrecision } from "@blueprintjs/datetime"
import CustomButton from "components/Atoms/CustomButton"
import CustomDatePicker from "components/Atoms/CustomDatePicker"
import CustomDialog from "components/Atoms/CustomDialog"
import Note from "components/Atoms/Note"
import ButtonMutation from "components/Molecules/ButtonMutation"
import dayjs from "dayjs"
import {
  CreateDemandMutationVariables,
  MediaEntityMediaType,
} from "generated/graphql"
import { ADD_DEMAND } from "graphql/demand/demand.mutations.gql"
import {
  DEMAND_LIST,
  DEMAND_LIST_INITIAL_VARIABLES,
} from "graphql/demand/demand.queries.gql"
import { useFormDisabled } from "hooks/useAuth"
import { getErrors } from "hooks/useErrors"
import { get, sumBy } from "lodash-es"
import demandModel from "models/molecules/demand.model"
import { Warning } from "@phosphor-icons/react"
import React, { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import AppToaster from "utils/AppToaster"
import { graphqlError } from "utils/parseError"
import SummaryRow from "./SummaryRow"

export const WholeSaleDemand = ({ className = null }) => {
  return (
    <Note
      className={className}
      intent="warning"
      textClassName="!flex gap-1 items-center"
    >
      <Warning weight="fill" />
      For wholesale demand broadcast
    </Note>
  )
}
const DemandSummary = ({
  demandDetails,
  isPriceRange,
  filter,
  isWholesale,
}) => {
  const navigate = useNavigate()
  const [isOpen, toggleOpen] = useState(false)
  const parsedVariables: CreateDemandMutationVariables = useMemo(() => {
    return {
      shouldConsolidate: demandDetails?.shouldConsolidate ?? false,
      name: demandDetails?.name,
      dateRange: demandDetails?.dateRange,
      broadcastTime: demandDetails?.broadcastTime,
      gradePrices: demandDetails.gradePrices?.map(gp => ({
        ...gp,
        grade: parseInt(get(gp, "grade.id")),
        priceUnit: parseInt(get(gp, "priceUnit.id")),
        priceLimit: !isPriceRange ? null : gp?.priceLimit,
      })),
      varietyGroup: demandDetails?.varietyGroup?.id,
      depot: demandDetails?.depot?.id,
      companyId: demandDetails?.company?.id,
      timeSlots: demandDetails?.timeSlots?.map(ts => ({
        quantity: get(ts, "quantity", 0),
        quantityTolerance: get(filter, "quantityTolerance", 0),
        timeSlot: get(ts.timeSlot, "id"),
        quantityUnit: get(ts, "quantityUnit.id", null),
      })),
      groupVisibility: filter.groupVisibility.map(group => group && group.id),
      companyVisibility: filter.companyVisibility.map(c => c && c.id),
      bookingQuantityLimit: filter?.bookingQuantityLimit,
      description: demandDetails?.description,
      attachments: demandDetails?.attachments
        ? [
            {
              id: demandDetails?.attachments?.id,
              url: demandDetails?.attachments,
              mediaType: MediaEntityMediaType.FLE,
            },
          ]
        : undefined,
    }
  }, [demandDetails, filter, isPriceRange])

  const { depot, varietyGroup: variety, timeSlots, gradePrices } = demandDetails
  const dateRange = get(demandDetails, "dateRange", [])
  const defaultGrade = get(gradePrices, 0, {})
  const totalDemand = sumBy(timeSlots, (ts: any) => ts.quantity)
  const computeDemandDays = (from, to) => {
    const d1 = dayjs(from)
    const d2 = dayjs(to)
    const difference = d1.diff(d2, "days")
    return difference === 1 ? `${difference} day` : `${difference} days`
  }
  const demandDays = useMemo(
    () => computeDemandDays(dateRange[1], dateRange[0]),
    [dateRange]
  )

  const disabled = useFormDisabled("demand", parsedVariables)

  return (
    <>
      <CustomButton icon="airplane" dark onClick={e => toggleOpen(true)}>
        Broadcast
      </CustomButton>
      <CustomDialog
        scrollable={false}
        width={"40%"}
        icon={
          <Icon
            icon="globe-network"
            intent="success"
            size={IconSize.STANDARD}
          />
        }
        title="Broadcast"
        light={true}
        isOpen={isOpen}
        onClose={e => toggleOpen(false)}
        footerRightButton={
          <ButtonMutation
            disabled={disabled}
            icon="plus"
            mutation={ADD_DEMAND}
            variables={parsedVariables}
            validate={state => {
              const errors = getErrors({
                schema: demandModel,
                state,
                abortEarly: true,
              })
              if (errors.latest) {
                AppToaster.show({
                  message: errors.latest,
                  intent: "danger",
                  icon: "cross",
                })
                return false
              }

              if (filter.groupVisibility.length === 0) {
                AppToaster.show({
                  message: "The demand group be visble to atleast one group",
                  intent: "danger",
                })
              } else {
                const validGroups = filter.groupVisibility.some(g => g)
                if (validGroups) return true
                AppToaster.show({
                  message: "Invalid group for visbility filter",
                  intent: "danger",
                })
                return false
              }
            }}
            refetchQueries={[
              {
                query: DEMAND_LIST,
                variables: DEMAND_LIST_INITIAL_VARIABLES,
              },
            ]}
            onError={err => {
              AppToaster.show({
                message: graphqlError(err.message),
                intent: "danger",
              })
            }}
            onCompleted={data => {
              const myDemand = get(data, "addDemand.demands.0.name")
              AppToaster.show({
                message: `Demand ${myDemand} added successfully`,
                intent: "success",
              })
              navigate("/d/demands")
            }}
          >
            Add Demand
          </ButtonMutation>
        }
      >
        {isWholesale && <WholeSaleDemand className="mb-3" />}
        <div>
          <SummaryRow label="Demand Date Range :">
            {dateRange[0]} to {dateRange[1]}
            <span className="font-600"> ({demandDays})</span>
          </SummaryRow>
          <SummaryRow label="Depot :">{depot?.name}</SummaryRow>
          <hr />
          <SummaryRow label="Variety Group:">
            <span className="vt-highlight">{variety.name}</span>
          </SummaryRow>
          <SummaryRow label="Harvest Grade :">
            {get(defaultGrade, "grade.name")} @{" "}
            <span className="vt-highlight">
              &#8377;{defaultGrade.price}{" "}
              {defaultGrade.priceLimit ? `- ${defaultGrade.priceLimit}` : null}/{" "}
              {get(defaultGrade, "priceUnit.name")}
            </span>
          </SummaryRow>
          <hr />
          <SummaryRow label="Time Slots" />
          {timeSlots.map((ts, idx) => {
            return (
              <div key={ts.id ? ts.id : `ts-${idx}`}>
                {ts.timeSlot && (
                  <SummaryRow className="mb-0" label={get(ts, "timeSlot.name")}>
                    {ts.quantity} / {get(ts, "quantityUnit.name")}
                  </SummaryRow>
                )}
              </div>
            )
          })}
          <hr />
          <SummaryRow label="Total Booking">
            <span className="vt-highlight">{totalDemand} Units</span>
          </SummaryRow>
          <hr />
          <SummaryRow label="Attachment Uploaded">
            <span className="vt-highlight">
              {demandDetails?.attachments ? "Yes" : "No"}
            </span>
          </SummaryRow>
          <hr />
          <SummaryRow label="Should Consolidate">
            <span className="vt-highlight">
              {demandDetails?.shouldConsolidate ? "Yes" : "No"}
            </span>
          </SummaryRow>
          <div className="flex justify-center items-center">
            <CustomDatePicker
              className="mt-6 vt-broadcast-time-picker"
              format="MMMM Do YYYY, h:mm:ss A"
              label="Broadcast Time"
              path="broadcastTime"
              valueAs="object"
              buttonProps={{ pill: true, icon: "cell-tower" }}
              minDate={new Date()}
              maxDate={dayjs().add(7, "d").toDate()}
              popoverProps={{
                position: Position.LEFT,
              }}
              closeOnSelect={false}
              timePickerProps={{
                useAmPm: true,
                showArrowButtons: true,
                precision: TimePrecision.SECOND,
              }}
            />
          </div>
        </div>
      </CustomDialog>
    </>
  )
}

export default DemandSummary
